/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable eqeqeq */
import React, { useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Bold,
  Essentials,
  Italic,
  Mention,
  Paragraph,
  Undo,
  Heading,
  Link,
  List,
  Image,
  ImageToolbar,
  ImageCaption,
  ImageStyle,
  ImageResize,
  ImageUpload,
  Underline,
  Strikethrough,
  BlockQuote,
  Alignment,
  SourceEditing,
  Table,
  TableToolbar,
  TableProperties,
  TableCellProperties,
  FontFamily,
  FontSize,
  FontColor,
  FontBackgroundColor,
  Indent,
  IndentBlock,
  Code,
  CodeBlock,
  RemoveFormat,
  HorizontalLine,
  SpecialCharacters,
  SpecialCharactersEssentials,
  Subscript,
  Superscript,
  TodoList,
  MediaEmbed,
  PageBreak,
  FindAndReplace,
  SelectAll,
  ShowBlocks,
  PasteFromOffice,
  Clipboard,
  ClipboardPipeline,
  GeneralHtmlSupport,
  ShiftEnter,
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";
import axios from "axios";
import useCommonApi from "../hooks/common-api";
import SuccessModal from "../components/SuccessModal";
import FailureModal from "../components/FailureModal";
import Loader from "../components/Loader";

const pageTitles = {
  "saas-agreement": "SaaS Agreement",
  terms: "Terms & Conditions",
  privacy: "Privacy Policy",
};

// Image upload helper (same behavior as BlogPage)
const uploadImage = async (file) => {
  let formdata = new FormData();
  formdata.append("vsn_file", file);

  const response = await axios({
    url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/upload/files/editorimage`,
    method: "POST",
    headers: {
      "Content-Type": "multipart/form-data",
      jwt_token: localStorage.getItem("admin_user_token"),
    },
    timeout: 120000,
    data: formdata,
  });

  if (!response.data.error) {
    return {
      default: response.data.file_path,
    };
  } else {
    throw new Error(response.data.message);
  }
};

// Custom Upload Adapter for CKEditor (same pattern as BlogPage)
class CustomUploadAdapter {
  constructor(loader) {
    this.loader = loader;
  }

  upload() {
    return this.loader.file.then(
      (file) =>
        new Promise((resolve, reject) => {
          uploadImage(file)
            .then((response) => {
              resolve({
                default: response.default,
              });
            })
            .catch((error) => {
              reject(error);
            });
        })
    );
  }

  abort() {
    // Handle abort
  }
}

// Custom Upload Adapter Plugin
function CustomUploadAdapterPlugin(editor) {
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
    return new CustomUploadAdapter(loader);
  };
}

// CKEditor configuration (reused from BlogPage)
const editorConfiguration = {
  licenseKey: "GPL",
  plugins: [
    Bold,
    Essentials,
    Italic,
    Paragraph,
    Undo,
    Heading,
    Link,
    List,
    TodoList,
    Image,
    ImageToolbar,
    ImageCaption,
    ImageStyle,
    ImageResize,
    ImageUpload,
    Underline,
    Strikethrough,
    BlockQuote,
    Alignment,
    SourceEditing,
    Table,
    TableToolbar,
    TableProperties,
    TableCellProperties,
    FontFamily,
    FontSize,
    FontColor,
    FontBackgroundColor,
    Indent,
    IndentBlock,
    Code,
    CodeBlock,
    RemoveFormat,
    HorizontalLine,
    SpecialCharacters,
    SpecialCharactersEssentials,
    Subscript,
    Superscript,
    MediaEmbed,
    PageBreak,
    FindAndReplace,
    SelectAll,
    ShowBlocks,
    PasteFromOffice,
    Clipboard,
    ClipboardPipeline,
    GeneralHtmlSupport,
    ShiftEnter,
    Mention,
    CustomUploadAdapterPlugin,
  ],
  toolbar: {
    items: [
      "undo",
      "redo",
      "|",
      "findAndReplace",
      "selectAll",
      "|",
      "heading",
      "|",
      "fontSize",
      "fontFamily",
      "fontColor",
      "fontBackgroundColor",
      "|",
      "bold",
      "italic",
      "underline",
      "strikethrough",
      "subscript",
      "superscript",
      "code",
      "|",
      "removeFormat",
      "|",
      "link",
      "uploadImage",
      "mediaEmbed",
      "insertTable",
      "blockQuote",
      "codeBlock",
      "horizontalLine",
      "pageBreak",
      "|",
      "alignment",
      "|",
      "bulletedList",
      "numberedList",
      "todoList",
      "|",
      "outdent",
      "indent",
      "|",
      "specialCharacters",
      "|",
      "sourceEditing",
      "showBlocks",
    ],
    shouldNotGroupWhenFull: true,
  },
  fontSize: {
    options: [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48],
    supportAllValues: true,
  },
  fontFamily: {
    options: [
      "default",
      "Arial, Helvetica, sans-serif",
      "Courier New, Courier, monospace",
      "Georgia, serif",
      "Lucida Sans Unicode, Lucida Grande, sans-serif",
      "Tahoma, Geneva, sans-serif",
      "Times New Roman, Times, serif",
      "Trebuchet MS, Helvetica, sans-serif",
      "Verdana, Geneva, sans-serif",
      "Roboto, sans-serif",
      "Open Sans, sans-serif",
      "Lato, sans-serif",
      "Montserrat, sans-serif",
    ],
    supportAllValues: true,
  },
  fontColor: {
    colors: [
      { color: "hsl(0, 0%, 0%)", label: "Black" },
      { color: "hsl(0, 0%, 30%)", label: "Dim grey" },
      { color: "hsl(0, 0%, 60%)", label: "Grey" },
      { color: "hsl(0, 0%, 90%)", label: "Light grey" },
      { color: "hsl(0, 0%, 100%)", label: "White", hasBorder: true },
      { color: "hsl(0, 75%, 60%)", label: "Red" },
      { color: "hsl(30, 75%, 60%)", label: "Orange" },
      { color: "hsl(60, 75%, 60%)", label: "Yellow" },
      { color: "hsl(90, 75%, 60%)", label: "Light green" },
      { color: "hsl(120, 75%, 60%)", label: "Green" },
      { color: "hsl(150, 75%, 60%)", label: "Aquamarine" },
      { color: "hsl(180, 75%, 60%)", label: "Turquoise" },
      { color: "hsl(210, 75%, 60%)", label: "Light blue" },
      { color: "hsl(240, 75%, 60%)", label: "Blue" },
      { color: "hsl(270, 75%, 60%)", label: "Purple" },
    ],
  },
  fontBackgroundColor: {
    colors: [
      { color: "hsl(0, 0%, 0%)", label: "Black" },
      { color: "hsl(0, 0%, 30%)", label: "Dim grey" },
      { color: "hsl(0, 0%, 60%)", label: "Grey" },
      { color: "hsl(0, 0%, 90%)", label: "Light grey" },
      { color: "hsl(0, 0%, 100%)", label: "White", hasBorder: true },
      { color: "hsl(0, 75%, 60%)", label: "Red" },
      { color: "hsl(30, 75%, 60%)", label: "Orange" },
      { color: "hsl(60, 75%, 60%)", label: "Yellow" },
      { color: "hsl(90, 75%, 60%)", label: "Light green" },
      { color: "hsl(120, 75%, 60%)", label: "Green" },
      { color: "hsl(150, 75%, 60%)", label: "Aquamarine" },
      { color: "hsl(180, 75%, 60%)", label: "Turquoise" },
      { color: "hsl(210, 75%, 60%)", label: "Light blue" },
      { color: "hsl(240, 75%, 60%)", label: "Blue" },
      { color: "hsl(270, 75%, 60%)", label: "Purple" },
    ],
  },
  heading: {
    options: [
      { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
      {
        model: "heading1",
        view: "h1",
        title: "Heading 1",
        class: "ck-heading_heading1",
      },
      {
        model: "heading2",
        view: "h2",
        title: "Heading 2",
        class: "ck-heading_heading2",
      },
      {
        model: "heading3",
        view: "h3",
        title: "Heading 3",
        class: "ck-heading_heading3",
      },
      {
        model: "heading4",
        view: "h4",
        title: "Heading 4",
        class: "ck-heading_heading4",
      },
      {
        model: "heading5",
        view: "h5",
        title: "Heading 5",
        class: "ck-heading_heading5",
      },
      {
        model: "heading6",
        view: "h6",
        title: "Heading 6",
        class: "ck-heading_heading6",
      },
    ],
  },
  image: {
    toolbar: [
      "imageStyle:inline",
      "imageStyle:block",
      "imageStyle:side",
      "|",
      "toggleImageCaption",
      "imageTextAlternative",
      "|",
      "resizeImage",
    ],
    resizeOptions: [
      {
        name: "resizeImage:original",
        label: "Original",
        value: null,
      },
      {
        name: "resizeImage:25",
        label: "25%",
        value: "25",
      },
      {
        name: "resizeImage:50",
        label: "50%",
        value: "50",
      },
      {
        name: "resizeImage:75",
        label: "75%",
        value: "75",
      },
    ],
  },
  alignment: {
    options: ["left", "center", "right", "justify"],
  },
  table: {
    contentToolbar: [
      "tableColumn",
      "tableRow",
      "mergeTableCells",
      "tableProperties",
      "tableCellProperties",
    ],
  },
  link: {
    defaultProtocol: "https://",
    decorators: {
      openInNewTab: {
        mode: "manual",
        label: "Open in a new tab",
        attributes: {
          target: "_blank",
          rel: "noopener noreferrer",
        },
      },
    },
  },
  htmlSupport: {
    allow: [
      {
        name: /.*/,
        attributes: true,
        classes: true,
        styles: true,
      },
    ],
    disallow: [],
  },
};

function DynamicPage({ pageType }) {
  const [editorData, setEditorData] = useState("<p></p>");
  const [initialEditorData, setInitialEditorData] = useState("<p></p>");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [openSuccessModal, setopenSuccessModal] = useState(undefined);
  const [openFailureModal, setopenFailureModal] = useState(undefined);
  const [successFailureMessage, setsuccessFailureMessage] = useState("");
  const editorRef = useRef(null);
  const { getDynamicPage, saveDynamicPage } = useCommonApi();

  const title = pageTitles[pageType] || "Page";

  // Fetch existing page content
  useEffect(() => {
    let isMounted = true;

    const fetchPage = async () => {
      setInitialLoading(true);
      try {
        const page = await getDynamicPage(pageType);
        if (!isMounted) return;

        if (page) {
          const content = page.page_content || "<p></p>";
          setEditorData(content);
          setInitialEditorData(content);
          setHasChanges(false);
        } else {
          setEditorData("<p></p>");
          setInitialEditorData("<p></p>");
          setHasChanges(false);
        }
      } catch (error) {
        console.log("Error fetching dynamic page:", error);
      } finally {
        if (isMounted) {
          setInitialLoading(false);
        }
      }
    };

    fetchPage();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageType]);

  // Track content changes to enable/disable save button
  useEffect(() => {
    const changed = editorData !== initialEditorData;
    setHasChanges(changed);
  }, [editorData, initialEditorData]);

  const removeModal = () => {
    setopenSuccessModal(undefined);
    setopenFailureModal(undefined);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        page_type: pageType,
        page_title: title,
        page_content: editorData,
      };

      const res = await saveDynamicPage(payload);
      if (!res.error) {
        setsuccessFailureMessage(res.message);
        setopenSuccessModal("dismissible");
        setInitialEditorData(editorData);
        setHasChanges(false);
      } else {
        setsuccessFailureMessage(res.message);
        setopenFailureModal("dismissible");
      }
    } catch (error) {
      console.log("Error saving dynamic page:", error);
      setsuccessFailureMessage("Failed to save page");
      setopenFailureModal("dismissible");
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "60vh" }}
      >
        <Loader />
      </div>
    );
  }

  return (
    <div className="mt-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0">{title}</h4>
        <Button
          type="button"
          className="grd-btn py-2"
          onClick={handleSave}
          style={{maxWidth: "150px"}}
          disabled={!hasChanges || loading}
        >
          {loading ? <Loader /> : "Save"}
        </Button>
      </div>

      <div className="my-3">
        <style>
          {`
            .ck-editor__editable {
              min-height: 70vh;
            }

            .ck.ck-editor__top {
              position: sticky !important;
              top: 65px !important;
              z-index: 1000 !important;
            }
          `}
        </style>
        <CKEditor
          editor={ClassicEditor}
          config={editorConfiguration}
          data={editorData}
          onReady={(editor) => {
            editorRef.current = editor;
            const editableElement = editor.editing.view.document.getRoot();
            editor.editing.view.change((writer) => {
              writer.setStyle("white-space", "normal", editableElement);
            });
          }}
          onChange={(event, editor) => {
            const data = editor.getData();
            setEditorData(data);
          }}
        />
      </div>

      <SuccessModal
        open={openSuccessModal}
        removeModal={removeModal}
        successFailureMessage={successFailureMessage}
      />
      <FailureModal
        open={openFailureModal}
        removeModal={removeModal}
        successFailureMessage={successFailureMessage}
      />
    </div>
  );
}

export default DynamicPage;

